import ky, { HTTPError } from "ky";
import type { z } from "zod";
import { queryOptions } from "@tanstack/react-query";
import {
  apiErrorSchema,
  eventsSchema,
  type HistorySearch,
  recordingsSchema,
  recordingPreviewSchema,
  consentSchema,
  approvalSchema,
  aliasesSchema,
  identitySchema,
  inventorySchema,
  ruleSchema,
  serverSchema,
  stateSchema,
  type Rule,
  type Server,
} from "./schemas";

export const transport = ky.create({
  credentials: "same-origin",
  timeout: 35_000,
  retry: { limit: 1, methods: ["get"] },
});
export async function read<S extends z.ZodType>(path: string, schema: S): Promise<z.output<S>> {
  return schema.parse(await transport.get(path).json());
}
function historyURL(path: string, filters: HistorySearch = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters))
    if (value && value !== "all") params.set(key, value);
  return path + (params.size ? "?" + params.toString() : "");
}
export const accessAPI = {
  recordings: (filters: HistorySearch = {}) =>
    read(historyURL("/api/admin/recordings", filters), recordingsSchema),
  events: (filters: HistorySearch = {}) =>
    read(historyURL("/api/admin/events", filters), eventsSchema),
  recording: (id: string) =>
    read("/api/admin/recordings/" + encodeURIComponent(id), recordingPreviewSchema),
  recordingDownload: (id: string) =>
    "/api/admin/recordings/" + encodeURIComponent(id) + "/download",
  consent: () => read("/api/device/consent", consentSchema),
  approveDevice: async (value: { csrf: string; approve: boolean }) =>
    approvalSchema.parse(await transport.post("/api/device/approve", { json: value }).json()),
  me: () => read("/api/me", identitySchema),
  state: () => read("/api/admin/state", stateSchema),
  inventory: () => read("/api/admin/netbird", inventorySchema),
  aliases: () => read("/api/aliases", aliasesSchema),
  createServer: async (value: Server) =>
    serverSchema.parse(
      await transport.post("/api/admin/servers", { json: serverSchema.parse(value) }).json(),
    ),
  updateServer: async (value: Server) =>
    serverSchema.parse(
      await transport
        .put("/api/admin/servers/" + encodeURIComponent(value.id), {
          json: serverSchema.parse(value),
        })
        .json(),
    ),
  createRule: async (value: Rule) =>
    ruleSchema.parse(
      await transport.post("/api/admin/rules", { json: ruleSchema.parse(value) }).json(),
    ),
  updateRule: async (value: Rule) =>
    ruleSchema.parse(
      await transport
        .put("/api/admin/rules/" + encodeURIComponent(value.id), { json: ruleSchema.parse(value) })
        .json(),
    ),
  /** @deprecated Use createServer or updateServer explicitly. */
  saveServer: async (value: Server) =>
    serverSchema.parse(
      await transport
        .put("/api/admin/servers/" + encodeURIComponent(value.id), {
          json: serverSchema.parse(value),
        })
        .json(),
    ),
  /** @deprecated Use createRule or updateRule explicitly. */
  saveRule: async (value: Rule) =>
    ruleSchema.parse(
      await transport
        .put("/api/admin/rules/" + encodeURIComponent(value.id), { json: ruleSchema.parse(value) })
        .json(),
    ),
  reconcile: () => transport.post("/api/admin/reconcile", { json: {} }).json(),
  logout: () => transport.post("/api/logout", { json: {} }),
};
export const queries = {
  recordings: (filters: HistorySearch = {}) =>
    queryOptions({
      queryKey: ["recordings", filters],
      queryFn: () => accessAPI.recordings(filters),
      refetchInterval: 15000,
      retry: false,
    }),
  events: (filters: HistorySearch = {}) =>
    queryOptions({
      queryKey: ["events", filters],
      queryFn: () => accessAPI.events(filters),
      refetchInterval: 15_000,
      retry: false,
    }),
  recording: (id: string) =>
    queryOptions({
      queryKey: ["recording", id],
      queryFn: () => accessAPI.recording(id),
      retry: false,
    }),
  consent: () =>
    queryOptions({ queryKey: ["device-consent"], queryFn: accessAPI.consent, retry: false }),
  me: () => queryOptions({ queryKey: ["identity"], queryFn: accessAPI.me, retry: false }),
  state: () =>
    queryOptions({
      queryKey: ["access", "state"],
      queryFn: accessAPI.state,
      refetchInterval: 15_000,
    }),
  inventory: () =>
    queryOptions({
      queryKey: ["access", "inventory"],
      queryFn: accessAPI.inventory,
      staleTime: 30_000,
    }),
  aliases: () => queryOptions({ queryKey: ["access", "aliases"], queryFn: accessAPI.aliases }),
};
export function isUnauthorized(error: unknown): boolean {
  return error instanceof HTTPError && error.response.status === 401;
}
export function errorMessage(error: unknown): string {
  if (error instanceof HTTPError) {
    const parsed = apiErrorSchema.safeParse(error.data);
    if (parsed.success) return parsed.data.error;
    if (
      typeof error.data === "string" &&
      error.data.length < 300 &&
      !error.data.trim().startsWith("<")
    )
      return error.data.trim();
    return error.response.status === 403
      ? "Your account needs permission for this action."
      : `Request failed (${error.response.status}). Refresh or check service health.`;
  }
  return error instanceof Error ? error.message : "The request could not be completed.";
}
